import React, { ReactNode, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import AppBar from '@mui/material/AppBar';
import useMediaQuery from '@mui/material/useMediaQuery';
import { SxProps, Theme, styled } from '@mui/material';
import Paper, { PaperProps } from '@mui/material/Paper';
import './TwDialog.scss';
import Draggable from 'react-draggable';

export type TwDialogSizes = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export interface TwDialogProps {
  /**
   * Sets the dialog open if true.
   */
  open: boolean;
  /**
   * Sets the dialog size to fullScreen.
   */
  fullScreen?: boolean;
  /**
   * Sets the dialog size.
   */
  size?: TwDialogSizes;
  /**
   * Title of the dialog if any.
   */
  title: string;
  /**
   * Callback fired when the component requests to be closed.
   */
  onClickClose?: () => void;
  /**
   * When true displays the close icon in the dialog app bar.
   */
  showCloseIcon?: boolean;
  /**
   * Main actions available in the dialog.
   */
  actions?: JSX.Element[];
  /**
   * Content/Component to be displayed in the middle of the top bar.
   */
  dialogTitleContent?: ReactNode;
  /**
   * Fullwidth action in the dialog.
   */
  fullWidthAction?: ReactNode;
  /**
   * Actions style for flex justify-content
   */
  actionsAlignment?: 'flex-end' | 'flex-start' | 'space-between';
  /**
   * Material Paper props
   */
  // eslint-disable-next-line @typescript-eslint/ban-types
  PaperProps?: Partial<PaperProps<'div', {}>>;
  /**
   * Sx props
   */
  sx?: SxProps;
  /**
   * hides AppBar
   */
  hideAppBar?: boolean;
  children: ReactNode;
  /**
   * className for actions container
   */
  classNameActions?: string;
  /**
   * boolean for if the dialog is draggable
   */
  draggable?: boolean;
}

const PaperComponent = (props: PaperProps): JSX.Element => {
  return (
    <Draggable cancel={'[class*="MuiDialogContent-root"]'} handle={`#${props.id}`}>
      <Paper {...props} id={props.id} />
    </Draggable>
  );
};

const StyledDialog = styled(Dialog)({
  '& .MuiToolbar-root': {
    justifyContent: 'space-between',
    paddingRight: '20px',
    paddingLeft: '20px',
    minHeight: '52px',
    height: '52px'
  },
  '& .MuiIconButton-root': {
    color: '#9090A6'
  },
  '& .MuiAppBar-root': {
    color: 'black',
    backgroundColor: 'white',
    boxShadow: 'none',
    borderBottom: '1px solid #E2E2EA'
  }
});

const TwDialog: React.FC<TwDialogProps> = ({
  open,
  fullScreen,
  size,
  title,
  actions,
  fullWidthAction,
  actionsAlignment,
  dialogTitleContent,
  showCloseIcon,
  onClickClose,
  PaperProps,
  sx,
  hideAppBar,
  classNameActions,
  children,
  draggable
}): JSX.Element => {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const isMobileOrTabletLandscape = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));

  const actionsClasses =
    actions || fullWidthAction
      ? `tw-dialog__actions tid-dialog__actions`
      : `tw-dialog__actions tw-dialog__noActionsPadding tid-dialog__actions`;

  const toolbarClass = isMobile ? 'tw-dialog__mobileToolbar' : '';
  const closeButtonClass = isMobile ? 'tw-dialog__mobileCloseButton' : '';
  const titleClass = isMobile ? `tw-dialog__title tw-dialog__mobileTitle` : `tw-dialog__title`;
  const dialogContentClass = isMobile
    ? `tw-dialog__mobileSteps tw-dialog__title-content`
    : 'tw-dialog__title-content';

  const handleClose = (): void => {
    onClickClose && onClickClose();
  };

  return (
    <StyledDialog
      className={fullScreen ? 'tid-dialog tid-dialog__fullscreen' : 'tid-dialog'}
      open={open}
      onClose={handleClose}
      maxWidth={size}
      PaperProps={{ ...PaperProps, id: title.replaceAll(' ', '-').replaceAll('&', 'and') }}
      fullWidth={true}
      fullScreen={fullScreen}
      sx={sx}
      PaperComponent={draggable && !isMobileOrTabletLandscape ? PaperComponent : Paper}
      onClick={(e): void => e.stopPropagation()}
    >
      {!hideAppBar && (
        <AppBar sx={{ position: 'relative' }}>
          <Toolbar className={`${toolbarClass}`}>
            <DialogTitle className={`${titleClass} tid-dialog__title`}>{title}</DialogTitle>
            {!!dialogTitleContent && (
              <div className={`${dialogContentClass}`}>{dialogTitleContent}</div>
            )}
            {showCloseIcon && (
              <IconButton
                edge="start"
                color="inherit"
                onClick={handleClose}
                aria-label="close"
                className={`${closeButtonClass}`}
              >
                <CloseIcon />
              </IconButton>
            )}
          </Toolbar>
        </AppBar>
      )}
      <DialogContent className={`tw-dialog__content tid-dialog__content`}>{children}</DialogContent>
      <DialogActions
        sx={{
          justifyContent: actionsAlignment ? actionsAlignment : 'flex-end',
          borderTop: fullScreen ? '1px solid #E2E2EA' : 'none'
        }}
        className={`${actionsClasses} ${classNameActions}`}
      >
        <>
          {fullWidthAction}
          {actions &&
            actions.map((action: JSX.Element, index: number) => (
              <div key={index} className="tid-dialog__action-item">
                {action}
              </div>
            ))}
        </>
      </DialogActions>
    </StyledDialog>
  );
};

TwDialog.defaultProps = {
  showCloseIcon: true,
  fullScreen: false,
  size: 'sm'
};

export default TwDialog;
